import { type RefObject, useRef } from 'react';

/**
 * Hook that creates a ref that always points to the latest value
 * This is useful when you need to access the most recent value in async callbacks
 * or event handlers without creating a new ref on every render
 *
 * @param value The value to store in the ref
 * @returns A ref object that always contains the current value
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const [count, setCount] = useState(0);
 *   const countRef = useFreshRef(count);
 *
 *   useEffect(() => {
 *     const timer = setTimeout(() => {
 *       console.log('Current count:', countRef.current); // Always has latest value
 *     }, 1000);
 *
 *     return () => clearTimeout(timer);
 *   }, []);
 *
 *   return <button onClick={() => setCount(c => c + 1)}>{count}</button>;
 * };
 * ```
 */
export const useFreshRef = <T>(value: T): RefObject<T> => {
  const ref = useRef<T>(value);
  ref.current = value;
  return ref;
};
